import type { IAdProvider, AdReward, AdRewardType, AdStatus, AdProviderConfig } from './AdProvider.js';

const TEST_AD_GROUP_ID = 'ait-ad-test-rewarded-id';

interface LoadFullScreenAdEvent {
  type: 'loaded';
}

interface ShowFullScreenAdEvent {
  type: 'requested' | 'show' | 'impression' | 'clicked' | 'dismissed' | 'failedToShow';
}

interface ShowFullScreenAdRewardEvent {
  type: 'userEarnedReward';
  data: { unitType: string; unitAmount: number };
}

type ShowAdEvent = ShowFullScreenAdEvent | ShowFullScreenAdRewardEvent;

interface LoadFullScreenAdParams {
  options: { adGroupId: string };
  onEvent: (event: LoadFullScreenAdEvent) => void;
  onError: (error: unknown) => void;
}

interface ShowFullScreenAdParams {
  options: { adGroupId: string };
  onEvent: (event: ShowAdEvent) => void;
  onError: (error: unknown) => void;
}

type LoadFullScreenAdFn = ((params: LoadFullScreenAdParams) => () => void) & {
  isSupported?: () => boolean;
};

type ShowFullScreenAdFn = ((params: ShowFullScreenAdParams) => () => void) & {
  isSupported?: () => boolean;
};

let loadFullScreenAd: LoadFullScreenAdFn | null = null;
let showFullScreenAd: ShowFullScreenAdFn | null = null;

/**
 * AppsInToss Ad Provider for Toss mini-app rewarded ads
 */
export class AppsInTossProvider implements IAdProvider {
  readonly name = 'AppsInTossProvider';
  private _status: AdStatus = 'idle';
  private currentReward: AdReward | null = null;
  private currentRewardType: AdRewardType = 'boost';
  private config: AdProviderConfig;
  private isInit = false;
  private cleanupFn: (() => void) | null = null;

  constructor(config: AdProviderConfig) {
    this.config = config;
  }

  private getAdGroupId(rewardType?: AdRewardType): string {
    const type = rewardType || this.currentRewardType;
    switch (type) {
      case 'boost':
        return this.config.appInTossBoostAdGroupId || TEST_AD_GROUP_ID;
      case 'docs':
        return this.config.appInTossDocsAdGroupId || TEST_AD_GROUP_ID;
      case 'ip':
        return this.config.appInTossIpAdGroupId || TEST_AD_GROUP_ID;
      default:
        return TEST_AD_GROUP_ID;
    }
  }

  get status(): AdStatus {
    return this._status;
  }

  private isAppsInTossEnvironment(): boolean {
    if (typeof window !== 'undefined') {
      const ua = navigator.userAgent.toLowerCase();
      if (ua.includes('toss') || ua.includes('appintoss')) return true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).__APPS_IN_TOSS__ || (window as any).AppsInToss) return true;
    }
    return false;
  }

  async initialize(): Promise<boolean> {
    if (!this.isAppsInTossEnvironment()) return false;

    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const framework = await import('@apps-in-toss/web-framework');

      if (framework.loadFullScreenAd && framework.showFullScreenAd) {
        loadFullScreenAd = framework.loadFullScreenAd as unknown as LoadFullScreenAdFn;
        showFullScreenAd = framework.showFullScreenAd as unknown as ShowFullScreenAdFn;

        const isLoadSupported = loadFullScreenAd.isSupported?.() !== false;
        const isShowSupported = showFullScreenAd.isSupported?.() !== false;

        if (isLoadSupported && isShowSupported) {
          this.isInit = true;
          console.log('[AppsInTossProvider] Initialized with ver2 API');
          return true;
        }
      }

      if (framework.GoogleAdMob) {
        const GoogleAdMob = framework.GoogleAdMob;
        if (GoogleAdMob.loadAppsInTossAdMob && GoogleAdMob.showAppsInTossAdMob) {
          loadFullScreenAd = GoogleAdMob.loadAppsInTossAdMob as unknown as LoadFullScreenAdFn;
          showFullScreenAd = GoogleAdMob.showAppsInTossAdMob as unknown as ShowFullScreenAdFn;

          const isLoadSupported = loadFullScreenAd.isSupported?.() !== false;
          const isShowSupported = showFullScreenAd.isSupported?.() !== false;

          if (isLoadSupported && isShowSupported) {
            this.isInit = true;
            console.log('[AppsInTossProvider] Initialized with legacy API');
            return true;
          }
        }
      }

      return false;
    } catch (error) {
      console.error('[AppsInTossProvider] Failed to initialize:', error);
      return false;
    }
  }

  isRewardedAdReady(): boolean {
    return this._status === 'ready';
  }

  async loadRewardedAd(rewardType?: AdRewardType): Promise<boolean> {
    if (!this.isInit || !loadFullScreenAd) return false;
    if (this._status === 'loading') return false;

    if (rewardType) this.currentRewardType = rewardType;
    this.cleanupFn?.();
    this.cleanupFn = null;

    const adGroupId = this.getAdGroupId(rewardType);
    this._status = 'loading';

    return new Promise((resolve) => {
      const cleanup = loadFullScreenAd!({
        options: { adGroupId },
        onEvent: (event) => {
          if (event.type === 'loaded') {
            this._status = 'ready';
            resolve(true);
          }
        },
        onError: () => {
          this._status = 'error';
          resolve(false);
        },
      });
      this.cleanupFn = cleanup;
    });
  }

  async showRewardedAd(rewardType?: AdRewardType): Promise<AdReward | null> {
    if (!this.isInit || !showFullScreenAd) return null;
    if (this._status !== 'ready') return null;

    const effectiveType = rewardType || this.currentReward?.type || this.currentRewardType;
    const adGroupId = this.getAdGroupId(effectiveType);
    this._status = 'showing';

    return new Promise((resolve) => {
      let rewardEarned = false;

      showFullScreenAd!({
        options: { adGroupId },
        onEvent: (event) => {
          switch (event.type) {
            case 'userEarnedReward':
              rewardEarned = true;
              break;
            case 'dismissed':
              this._status = 'idle';
              if (rewardEarned && this.currentReward) {
                const reward = this.currentReward;
                this.currentReward = null;
                resolve(reward);
              } else {
                resolve(null);
              }
              break;
            case 'failedToShow':
              this._status = 'idle';
              resolve(null);
              break;
          }
        },
        onError: () => {
          this._status = 'idle';
          resolve(null);
        },
      });
    });
  }

  setReward(reward: AdReward): void {
    this.currentReward = reward;
  }

  destroy(): void {
    this.cleanupFn?.();
    this.cleanupFn = null;
    this._status = 'idle';
    this.currentReward = null;
    this.isInit = false;
  }
}
